import { FC, Suspense, useEffect, useRef, useState } from 'react';
interface Props {
  filters: DiagnosticAssessmentTableFilters;
  hideButton?: number | null;
}

const PAGE_SIZE = 50;
export const DownloadCsvDiagnosticAssessmentHistory: FC<Props> = ({ filters, hideButton = null }) => {
  const [fetchData, setFetchData] = useState(false);
  const [localFilters, setLocalFilters] = useState({ ...filters, page: 0 });
  const [skillAssessmentFiltered, setSkillAssessmentsFiltered] = useState<DiagnosticSkillHistoryTableItem[]>([]);
  const [pageInfo, setPageInfo] = useState<PageInfo | undefined>(undefined);
  const [finishedLoading, setFinishedLoading] = useState(false);
  const dispatch = useAppDispatch();
  const isCsvProcessing = useAppSelector(selectedCsvProcess);

  const downloadToastRef = useRef<Toast | null>(null);
  const successfullToastRef = useRef<Toast | null>(null);

  const clear = async () => {
    setTimeout(() => {
      setSkillAssessmentsFiltered([]);
      setFetchData(false);
      setFinishedLoading(false);
      setPageInfo(undefined);
      downloadToastRef?.current?.clear();
    }, 500);
  };

  useEffect(() => {
    setLocalFilters({ ...filters, page: 0, rows: PAGE_SIZE });
  }, [filters]);

  useEffect(() => {
    if (skillAssessmentFiltered.length) {
      exportToCsvAssessmentHistoryTable(skillAssessmentFiltered, AssessmentHistoryType.DIAGNOSTIC_ASSESSMENTS)
        .then(() => {
          clear().then(() => {
            showSuccessfullToast(successfullToastRef);
            setTimeout(() => {
              dispatch(setCsvProcess(false));
              dispatch(setReportHubQuerysInProgress(false));
            }, 3000);
          });
        })
        .catch(() => {
          dispatch(setCsvProcess(false));
          dispatch(setReportHubQuerysInProgress(false));
        });
    }
  }, [skillAssessmentFiltered.length]);

  useEffect(() => {
    if (fetchData && pageInfo && !pageInfo.hasNextPage) {
      setFinishedLoading(true);
    }
  }, [pageInfo]);

  useEffect(() => {
    // emulated onClick for ReportHub feature
    if (hideButton) {
      dispatch(setCsvProcess(true));
      setFetchData(true);
      showDownloadToast(downloadToastRef);
    }
  }, [hideButton]);

  return (
    <>
      {!hideButton && (
        <Button
          icon="nc_icon-nc_export icon-sm"
          label="Export Data Table"
          tooltip="Export the filtered data to CSV format"
          aria-label="Export the filtered data to CSV format"
          rounded
          outlined
          disabled={fetchData || isCsvProcessing}
          onClick={() => {
            dispatch(setCsvProcess(true));
            setFetchData(true);
            showDownloadToast(downloadToastRef);
          }}
        ></Button>
      )}
      {fetchData && (
        <Suspense>
          <AssessmentHistoryDiagnosticQueryDataCsv
            finishedLoading={finishedLoading}
            fetchData={fetchData}
            filterState={localFilters}
            setSkillAssessments={setSkillAssessmentsFiltered}
            pageInfo={pageInfo}
            setPageInfo={setPageInfo}
          />
        </Suspense>
      )}
      <ToastExportXLSorCSV downloadToastRef={downloadToastRef} clear={clear} progressBarType="indeterminate" />
      <ToastSuccessful successfullToastRef={successfullToastRef} />
    </>
  );
};

import { Dispatch, SetStateAction } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { getSkillAssessmentSubstate, getSkillsDiagnosticHistoryCSV } from '../../graphQLOperations/Monitoring';
import { PageInfo } from '../../models/PageInfo';
import { TemporarySkillInterface, DiagnosticAssessmentTableFilters, DiagnosticSkillHistoryTableItem } from './models';
import { MonitoringSkillSubStateReporQuery } from '../../graphQLOperations/__generated__/MonitoringSkillSubStateReporQuery.graphql';
import { Button } from 'primereact/button';
import { MonitoringAllSkillsCSVQuery } from '../../graphQLOperations/__generated__/MonitoringAllSkillsCSVQuery.graphql';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { selectedCsvProcess, setCsvProcess } from '../../redux/reducers/csvProcessSlice';
import { Toast } from 'primereact/toast';
import ToastSuccessful from '../ExportCSV/ToastSuccessful';
import { showDownloadToast, showSuccessfullToast } from '../../businessLogicService/exportToastHandler';
import { exportToCsvAssessmentHistoryTable } from '../../businessLogicService/exportCSVAssessmentHistoryTable';
import { AssessmentHistoryType } from '../../models/CsvExport';
import { setReportHubQuerysInProgress } from '../../redux/reducers/reportHubQuerysInProgress';
import ToastExportXLSorCSV from '../ExportCSV/ToastExportXLSorCSV';

export interface QuerySkillDiagnositcDataProp {
  fetchData: boolean;
  filterState: DiagnosticAssessmentTableFilters;
  setSkillAssessments: Dispatch<SetStateAction<DiagnosticSkillHistoryTableItem[]>>;
  setPageInfo: Dispatch<SetStateAction<PageInfo | undefined>>;
  pageInfo: PageInfo | undefined;
  finishedLoading: boolean;
}

export const AssessmentHistoryDiagnosticQueryDataCsv = ({
  setSkillAssessments,
  filterState,
  fetchData,
  setPageInfo,
  pageInfo,
  finishedLoading,
}: QuerySkillDiagnositcDataProp) => {
  const isSkillCode = filterState.filters.skillName?.split('').every(char => !isNaN(Number(char)));
  const [tempData, setTempData] = useState<TemporarySkillInterface[]>([]);

  const allSkills = useLazyLoadQuery<MonitoringAllSkillsCSVQuery>(
    getSkillsDiagnosticHistoryCSV,
    {
      filter: {
        targetGroupIds: filterState.filters.roadmaps.length ? filterState.filters.roadmaps : null,
        standardGradeIds: filterState.filters.skillGrades?.length ? filterState.filters.skillGrades : null,
        skillName: !isSkillCode ? filterState.filters.skillName : null,
        skillCode: isSkillCode ? filterState.filters.skillName : null,
      },
      first: filterState.rows,
      order: [{ name: filterState.sortOrder === 1 ? 'ASC' : 'DESC' }],
      after: pageInfo?.endCursor,
    },
    { fetchPolicy: 'network-only', fetchKey: JSON.stringify(filterState) },
  );

  const skillSubstates = useLazyLoadQuery<MonitoringSkillSubStateReporQuery>(
    getSkillAssessmentSubstate,
    {
      filter: {
        skillIds: tempData.map(temp => temp.id),
        classIds: filterState.filters.classes,
        gradeLevelIds: filterState.filters.gradeLevels,
        skillResult: 'PLACED_OUT',
      },
      skip: !tempData.length || !finishedLoading,
    },
    { fetchPolicy: 'network-only', fetchKey: JSON.stringify(tempData) },
  );

  useEffect(() => {
    if (finishedLoading) {
      const newValues = tempData.map(item => {
        const studentReport = skillSubstates.studentsSkillSubStateReport?.find(report => report.skill.id === item.id);
        return { skill: item, placedOut: studentReport?.usersBySkillResults[0].value || [] };
      });

      setSkillAssessments([...newValues] as DiagnosticSkillHistoryTableItem[]);
      setTempData([]);
    }
  }, [skillSubstates]);

  useEffect(() => {
    if (allSkills.allSkills?.nodes?.length && fetchData) {
      setTempData([...tempData, ...((allSkills.allSkills?.nodes as TemporarySkillInterface[]) || [])]);
      setPageInfo(allSkills.allSkills.pageInfo);
    }
  }, [allSkills]);

  return <div style={{ display: 'none' }}></div>;
};
